import React, { createContext, useContext, useState, ReactNode } from 'react';

interface GlobalModalsContextType {
  // Category Management Modal
  isCategoryModalOpen: boolean;
  openCategoryModal: () => void;
  closeCategoryModal: () => void;
  
  // Out of Credits Modal
  isOutOfCreditsModalOpen: boolean;
  openOutOfCreditsModal: () => void;
  closeOutOfCreditsModal: () => void;
  
  // Invite Members Modal
  isInviteMembersModalOpen: boolean;
  inviteMembersWorkbookId: string | null;
  openInviteMembersModal: (workbookId: string, onSuccess?: () => void) => void;
  closeInviteMembersModal: () => void;
  onInviteSuccess: (() => void) | null;
}

const GlobalModalsContext = createContext<GlobalModalsContextType | undefined>(undefined);

export const useGlobalModals = () => {
  const context = useContext(GlobalModalsContext);
  if (context === undefined) {
    throw new Error('useGlobalModals must be used within a GlobalModalsProvider');
  }
  return context;
};

interface GlobalModalsProviderProps {
  children: ReactNode;
}

export const GlobalModalsProvider: React.FC<GlobalModalsProviderProps> = ({ children }) => {
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isOutOfCreditsModalOpen, setIsOutOfCreditsModalOpen] = useState(false);
  const [isInviteMembersModalOpen, setIsInviteMembersModalOpen] = useState(false);
  const [inviteMembersWorkbookId, setInviteMembersWorkbookId] = useState<string | null>(null);
  const [onInviteSuccess, setOnInviteSuccess] = useState<(() => void) | null>(null);

  const openCategoryModal = () => setIsCategoryModalOpen(true);
  const closeCategoryModal = () => setIsCategoryModalOpen(false);
  
  const openOutOfCreditsModal = () => setIsOutOfCreditsModalOpen(true);
  const closeOutOfCreditsModal = () => setIsOutOfCreditsModalOpen(false);
  
  const openInviteMembersModal = (workbookId: string, onSuccess?: () => void) => {
    setInviteMembersWorkbookId(workbookId);
    setOnInviteSuccess(() => onSuccess || null);
    setIsInviteMembersModalOpen(true);
  };
  
  const closeInviteMembersModal = () => {
    setIsInviteMembersModalOpen(false);
    setInviteMembersWorkbookId(null);
    setOnInviteSuccess(null);
  };

  const value: GlobalModalsContextType = {
    isCategoryModalOpen,
    openCategoryModal,
    closeCategoryModal,
    isOutOfCreditsModalOpen,
    openOutOfCreditsModal,
    closeOutOfCreditsModal,
    isInviteMembersModalOpen,
    inviteMembersWorkbookId,
    openInviteMembersModal,
    closeInviteMembersModal,
    onInviteSuccess,
  };

  return (
    <GlobalModalsContext.Provider value={value}>
      {children}
    </GlobalModalsContext.Provider>
  );
}; 